import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { eventService } from '../../../services/eventService';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { TextArea } from '../../../components/ui/TextArea';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const EventEditor = () => {
  const { id } = useParams();
  const isNew = !id;
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    event_type: 'conference',
    status: 'upcoming'
  });

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const data = await eventService.getEvent(id, user.id);
        setFormData({
          name: data.name || '',
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          start_time: data.start_time || '',
          end_time: data.end_time || '',
          location: data.location || '',
          description: data.description || '',
          event_type: data.event_type || 'conference',
          status: data.status || 'upcoming'
        });
      } catch (err) {
        setError('Failed to load event details.');
      } finally {
        setLoading(false);
      }
    };
    if (!isNew && user?.id) fetchEvent();
  }, [id, user, isNew]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!formData.name || !formData.start_date) {
      setError('Event name and start date are required.');
      return;
    }
    if (formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      setError('End date cannot be before start date.');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const created = await eventService.createEvent(user.id, formData);
        navigate(`/app/events/${created.id}`);
      } else {
        await eventService.updateEvent(id, user.id, formData);
        navigate(`/app/events/${id}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to save event.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  return (
    <div className="max-w-3xl mx-auto pb-12">
      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/events')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Events
        </button>
        {!isNew && (
          <>
            <span className="mx-2">/</span>
            <button onClick={() => navigate(`/app/events/${id}`)} className="hover:text-slate-900">
              {formData.name}
            </button>
          </>
        )}
      </div>

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">{isNew ? 'Create Event' : 'Edit Event'}</h1>
        <p className="mt-2 text-slate-600">Set up the context for your networking activity.</p>
      </header>

      {error && <Alert variant="error" message={error} className="mb-6" />}

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 md:p-8 space-y-6">
          <Input 
            label={<>Event Name <span className="text-red-500">*</span></>}
            value={formData.name}
            onChange={e => setFormData({...formData, name: e.target.value})}
            required
            placeholder="e.g. Global Tech Expo 2024"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              type="date"
              label={<>Start Date <span className="text-red-500">*</span></>}
              value={formData.start_date}
              onChange={e => setFormData({...formData, start_date: e.target.value})}
              required
            />
            <Input 
              type="date"
              label="End Date"
              value={formData.end_date}
              onChange={e => setFormData({...formData, end_date: e.target.value})}
            />
            <Input 
              type="time"
              label="Start Time"
              value={formData.start_time}
              onChange={e => setFormData({...formData, start_time: e.target.value})}
            />
            <Input 
              type="time"
              label="End Time"
              value={formData.end_time}
              onChange={e => setFormData({...formData, end_time: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              label="Location"
              value={formData.location}
              onChange={e => setFormData({...formData, location: e.target.value})}
              placeholder="e.g. San Francisco, CA"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Event Type</label>
              <select 
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
                value={formData.event_type}
                onChange={e => setFormData({...formData, event_type: e.target.value})}
              >
                <option value="conference">Conference</option>
                <option value="expo">Expo / Trade Show</option>
                <option value="workshop">Workshop</option>
                <option value="networking">Networking Event</option>
                <option value="meetup">Meetup</option>
                <option value="speaking">Speaking Engagement</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <TextArea 
            label="Description"
            value={formData.description}
            onChange={e => setFormData({...formData, description: e.target.value})}
            placeholder="Brief details about the event..."
            rows={3}
          />

          {!isNew && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
              <select 
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900"
                value={formData.status}
                onChange={e => setFormData({...formData, status: e.target.value})}
              >
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}
        </div>
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
          <Button type="submit" isLoading={saving}>{isNew ? 'Create Event' : 'Save Changes'}</Button>
        </div>
      </form>
    </div>
  );
};